/**
 * Which screen the app shows (decision 28: "open in another tab" with "use here"; part 06 notes; part 11 log, table S).
 * Pure: the workspace passes what it knows, and gets the screen and whether the writer may type.
 */
import type { SessionState } from '@cockatrice/storage';

/** How opening the local store went (S10, S11): open, a later build upgraded it, or the browser refused. */
export type StoreOpening = 'open' | 'outdated' | 'failed';

/**
 * A screen (S1–S11). `loading`: nothing to show yet. `workspace`: the book and editor. `elsewhere`: another tab edits,
 * with "use here". `outdated`: a later build upgraded the store, reload. `failed`: the store can't open or Web Locks is
 * missing. `closed`: the page is going away.
 */
export type Screen = 'loading' | 'workspace' | 'elsewhere' | 'outdated' | 'failed' | 'closed';

/** What the workspace knows that decides the screen. */
export interface ScreenInput {
  /** Null until the store is open or refused. */
  readonly opening: StoreOpening | null;
  /** The session's state; null before a session exists. */
  readonly session: SessionState | null;
  /** Whether the book has been read from the store; it then stays on screen, paused, whenever the tab isn't editing. */
  readonly loaded: boolean;
}

/** The screen for `input` (table S). */
export function screenFor(input: ScreenInput): Screen {
  if (input.opening === 'outdated') return 'outdated';
  if (input.opening === 'failed') return 'failed';
  switch (input.session) {
    case null:
      return 'loading';
    // S0–S3, S7: once the book has been read it stays on screen, paused, while a session starts (a new one, or "use
    // here"), hands over or reconnects; before that, loading.
    case 'starting':
    case 'active':
    case 'releasing':
    case 'disconnected':
      return input.loaded ? 'workspace' : 'loading';
    case 'elsewhere':
    case 'stale':
    case 'failed':
      return 'elsewhere';
    case 'outdated':
      return 'outdated';
    case 'closed':
      return 'closed';
  }
}

/** Whether the writer may type and change the book (S1–S9): only while this tab holds the lock and the book is read. */
export function canEdit(input: ScreenInput): boolean {
  return input.opening === 'open' && input.session === 'active' && input.loaded;
}
